import { dataFields } from '../config/dataFields';

type RawDataField = {
	label: string;
	dataField: string;
};

export type MergeTagGroup =
	| 'contact'
	| 'listing'
	| 'user'
	| 'current_date'
	| 'legal_compliance'
	| 'other';

export interface MergeTagDictionaryEntry {
	key: string;
	label: string;
	value: string;
	translation: string;
	group: MergeTagGroup;
	canonicalKey: string;
	isImageField: boolean;
}

const GROUP_LABELS: Record<string, string> = {
	contact: 'Recipient Data',
	listing: 'Listing Data',
	user: 'User Data',
	current_date: 'General Date / Time',
	legal_compliance: 'Legal & Compliance',
};

const GROUP_ORDER = [
	'contact',
	'listing',
	'user',
	'current_date',
	'legal_compliance',
] as const;

const IMAGE_FIELD_KEYS = new Set([
	'listing.image_data_0',
	'listing.image_data_1',
	'listing.image_data_2',
	'listing.image_data_3',
	'listing.image_data_4',
	'listing.image_data_5',
	'listing.image_data_6',
	'listing.image_data_7',
	'listing.image_data_8',
	'listing.image_data_9',
	'listing.imagePath',
	'listing.thumbPath',
	'listing.imageUrl',
	'listing.image',
	'listing.photo',
	'listing.listingImage',
	'listing.mainImage',
	'listing.primaryImage',
	'listing.agentImagePath',
	'listing.secondAgentImagePath',
	'listing.officeLogoDark',
	'listing.officeLogoLight',
	'user.imageUrl',
	'user.profileImage',
	'user.profilePicture',
	'user.avatar',
	'user.profileImagePortrait',
	'user.profileImageSquare',
	'user.profileImageCircle',
	'user.photo',
	'user.logo',
	'user.companyLogo',
	'user.logoDarkUrl',
	'user.logoLightUrl',
	'user.officeLogoLightUrl',
	'user.officeLogoDarkUrl',
]);

const ALIAS_TO_CANONICAL: Record<string, string> = {
	'legal_compliance.campaignDisclaimer': 'campaignDisclaimer',
	'legal_compliance.unsubscribeLink': 'unsubscribeLink',
	'contact.zip': 'contact.zipCode',
	'contact.companyName': 'contact.company',
	'contact.companName': 'contact.company',
	'user.websiteUrl': 'user.website',
	'user.imageUrl': 'user.profileImagePortrait',
	'user.profileImage': 'user.profileImagePortrait',
	'user.profilePicture': 'user.profileImageSquare',
	'user.avatar': 'user.profileImageCircle',
	'user.photo': 'user.profileImagePortrait',
	'user.companyLogo': 'user.logo',
	'listing.imageUrl': 'listing.image_data_0',
	'listing.image': 'listing.image_data_0',
	'listing.photo': 'listing.image_data_0',
	'listing.listingImage': 'listing.image_data_0',
	'listing.mainImage': 'listing.image_data_0',
	'listing.primaryImage': 'listing.image_data_0',
	'listing.sqFoot': 'listing.squareFootage',
	// The names the legacy listing layouts emit into production HTML, mapped onto
	// the tags this dictionary actually carries. The send context answers them
	// already — these exist so the same tag also paints on the canvas and in
	// preview, instead of a designer seeing a raw token where the address goes.
	'listing.imagePath': 'listing.image_data_0',
	'listing.stateProvince': 'listing.state',
	'listing.postalCode': 'listing.zip',
	// Retired duplicates: the same column as the spec-named tag they point at,
	// kept so templates saved against the old key still resolve.
};

const FIELD_MAP = dataFields as Record<string, RawDataField>;

function getGroupFromKey(key: string): MergeTagGroup {
	if (key === 'campaignDisclaimer' || key === 'unsubscribeLink') {
		return 'legal_compliance';
	}

	const segment = key.includes('.') ? key.split('.')[0] : 'other';
	if (segment === 'contact') return 'contact';
	if (segment === 'listing') return 'listing';
	if (segment === 'user') return 'user';
	if (segment === 'current_date') return 'current_date';
	if (segment === 'legal_compliance') return 'legal_compliance';
	return 'other';
}

export function getMergeTagGroup(key: string): MergeTagGroup {
	return getGroupFromKey(key);
}

export function normalizeMergeTagKey(key: string): string {
	return ALIAS_TO_CANONICAL[key] || key;
}

export function hasMergeTagKey(key: string): boolean {
	return !!FIELD_MAP[key];
}

export function isImageMergeTagKey(key: string): boolean {
	const canonicalKey = normalizeMergeTagKey(key);
	return IMAGE_FIELD_KEYS.has(key) || IMAGE_FIELD_KEYS.has(canonicalKey);
}

export function getMergeTagLabel(key: string): string | null {
	const field = FIELD_MAP[key];
	if (field?.label) {
		return field.label;
	}

	const canonicalField = FIELD_MAP[normalizeMergeTagKey(key)];
	return canonicalField?.label || null;
}

export function getMergeTagTranslation(key: string): string {
	const field = FIELD_MAP[key] || FIELD_MAP[normalizeMergeTagKey(key)];
	return field?.dataField || normalizeMergeTagKey(key);
}

export function getMergeTagGroupLabel(group: string): string {
	return GROUP_LABELS[group] || group;
}

/**
 * How a chosen merge tag reads in a field the user picked it from — the group
 * and the field's own label, e.g. `{{listing.image_data_5}}` → "Listing Image 6".
 *
 * Group labels are written for menu headings ("Listing Data"), which reads wrong
 * as a prefix, so the trailing "Data" comes off.
 *
 * A key the dictionary doesn't know falls back to the bare key rather than an
 * empty string: a legacy or hand-entered tag stays visible and therefore
 * removable, instead of looking like an empty field with an image still bound.
 */
export function getMergeTagDisplayLabel(tag: string | null | undefined): string {
	const key = String(tag || '')
		.trim()
		.replace(/^\{\{|\}\}$/g, '')
		.trim();
	if (!key) return '';

	const label = getMergeTagLabel(key);
	if (!label) return key;

	const group = getMergeTagGroupLabel(getMergeTagGroup(key)).replace(/\s*Data$/i, '');
	return group ? `${group} ${label}` : label;
}

export function getMergeTagGroupSortWeight(group: string): number {
	const index = GROUP_ORDER.indexOf(group as (typeof GROUP_ORDER)[number]);
	return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

export function getMergeTagDictionaryEntries(options?: {
	excludeImageFields?: boolean;
}): MergeTagDictionaryEntry[] {
	const excludeImageFields = options?.excludeImageFields ?? false;

	const entries = Object.entries(FIELD_MAP)
		.map(([key, value]) => {
			const canonicalKey = normalizeMergeTagKey(key);
			const isImageField = isImageMergeTagKey(key);

			return {
				key,
				label: value.label,
				value: `{{${key}}}`,
				translation: value.dataField,
				group: getGroupFromKey(key),
				canonicalKey,
				isImageField,
			} satisfies MergeTagDictionaryEntry;
		})
		.filter(entry => {
			if (!excludeImageFields) return true;
			return !entry.isImageField;
		});

	return entries.sort((a, b) => {
		const groupCompare =
			getMergeTagGroupSortWeight(a.group) - getMergeTagGroupSortWeight(b.group);
		if (groupCompare !== 0) return groupCompare;

		return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
	});
}

export function dedupeMergeTagKeys(keys: string[]): string[] {
	const seen = new Set<string>();
	const result: string[] = [];

	keys.forEach(key => {
		const canonicalKey = normalizeMergeTagKey(key);
		if (seen.has(canonicalKey)) return;
		seen.add(canonicalKey);
		result.push(key);
	});

	return result;
}
